import { create } from 'zustand';

import type { PanelModeValue } from '../../bizy-side-panel/lib/panel-preferences';

// ─── Draft store ─────────────────────────────────────────────────────────────
//
// Holds pending (un-saved) changes for the Bizy settings General tab. Each
// section reads the draft if it exists, otherwise falls back to the current
// server / preference value. The bottom Save/Cancel bar is shown whenever any
// draft is present; Save commits everything, Cancel discards.

interface BizySettingsDraftState {
	pendingProfileFields: Record<string, string>;
	pendingDefaultChatSize: PanelModeValue | null;
	pendingReduceMotion: boolean | null;

	setPendingProfileField: (field: string, value: string) => void;
	clearPendingProfileField: (field: string) => void;
	setPendingDefaultChatSize: (value: PanelModeValue) => void;
	clearPendingDefaultChatSize: () => void;
	setPendingReduceMotion: (value: boolean) => void;
	clearPendingReduceMotion: () => void;

	clearAllDrafts: () => void;
}

const initialDraftState = {
	pendingProfileFields: {},
	pendingDefaultChatSize: null,
	pendingReduceMotion: null,
};

export const useBizySettingsDraftStore = create<BizySettingsDraftState>((set) => ({
	...initialDraftState,

	setPendingProfileField: (field, value) =>
		set((state) => ({
			pendingProfileFields: { ...state.pendingProfileFields, [field]: value },
		})),

	clearPendingProfileField: (field) =>
		set((state) => {
			if (!(field in state.pendingProfileFields)) return state;
			const next = { ...state.pendingProfileFields };
			delete next[field];
			return { pendingProfileFields: next };
		}),

	setPendingDefaultChatSize: (value) => set({ pendingDefaultChatSize: value }),
	clearPendingDefaultChatSize: () => set({ pendingDefaultChatSize: null }),

	setPendingReduceMotion: (value) => set({ pendingReduceMotion: value }),
	clearPendingReduceMotion: () => set({ pendingReduceMotion: null }),

	clearAllDrafts: () => set(initialDraftState),
}));

export const useBizySettingsHasDrafts = (): boolean =>
	useBizySettingsDraftStore(
		(state) => Object.keys(state.pendingProfileFields).length > 0 || state.pendingDefaultChatSize !== null || state.pendingReduceMotion !== null
	);
